import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

// How long authors can edit their own comments after posting.
const EDIT_WINDOW_MS = 5 * 60 * 1000

// Friendly relative time. Avoids a date-fns dependency for just this.
function relativeTime(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60)   return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60)   return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24)   return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7)    return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}

// Styles for each kind of timeline event. Icon + a one-line summary.
// Comments render fully below this; everything else is a compact line.
function renderEventLine(e, profilesById) {
  const who = e.author_id ? (profilesById[e.author_id]?.full_name || 'Someone') : 'System'
  switch (e.kind) {
    case 'created':
      return `${who} created this ticket`
    case 'status_changed': {
      const from = e.meta?.from || '—'
      const to   = e.meta?.to   || '—'
      return `${who} changed status: ${from.replace(/_/g,' ')} → ${to.replace(/_/g,' ')}`
    }
    case 'resolved':
      return `${who} resolved this ticket`
    case 'assigned': {
      const m = e.meta || {}
      const parts = []
      if (m.to_user_id) parts.push(profilesById[m.to_user_id]?.full_name || 'someone')
      if (m.to_team)    parts.push(`${m.to_team.replace(/_/g,' ')} team`)
      return `${who} assigned to ${parts.join(' / ') || 'nobody'}`
    }
    case 'unassigned':
      return `${who} unassigned`
    case 'image_added':
      return `${who} added an image`
    case 'image_deleted':
      return `${who} removed an image`
    default:
      return `${who} ${e.kind.replace(/_/g,' ')}`
  }
}

// Turn a comment body into HTML-safe nodes with @mentions highlighted.
// Returns an array of strings and React spans — we render it with
// dangerouslySetInnerHTML-free mapping so we don't re-XSS ourselves.
function renderCommentBody(body, profilesByName) {
  if (!body) return null
  // Split on @mentions. We match @ followed by 2+ non-space chars
  // greedily up to a word boundary, then look up by normalized name.
  const parts = body.split(/(@[a-zA-Z][a-zA-Z0-9_.\-' ]{1,40})/g)
  return parts.map((part, i) => {
    if (!part.startsWith('@')) return part
    const query = part.slice(1).trim().toLowerCase()
    // Longest-match: user names with spaces make this slightly tricky;
    // we accept the longest prefix that maps to a known user.
    let match = null
    for (const [norm, prof] of Object.entries(profilesByName)) {
      if (query.startsWith(norm) && (!match || norm.length > match.norm.length)) {
        match = { norm, prof }
      }
    }
    if (!match) return part  // Unrecognized — render as plain text.
    const rest = query.slice(match.norm.length)
    return (
      <span key={i}>
        <span style={{ background: '#EEF4FF', color: '#1E40AF', padding: '1px 4px', borderRadius: 3, fontWeight: 500 }}>
          @{match.prof.full_name}
        </span>
        {rest}
      </span>
    )
  })
}

export default function TicketActivity({ ticketId, currentUserId, currentUserRole, profiles }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState('')
  const [mentionMenu, setMentionMenu] = useState(null) // { index, query } | null
  const textareaRef = useRef(null)

  // Profile lookup indexes. profilesById for event rendering, profilesByName
  // for parsing @mentions in comment bodies.
  const profilesById = useMemo(() => {
    return Object.fromEntries(profiles.map(p => [p.id, p]))
  }, [profiles])
  const profilesByName = useMemo(() => {
    // Key by lowercased full name; we also add first-name-only aliases so
    // "@ian" matches "Ian Beason". First-name aliases only register if
    // they're unique — collisions fall back to requiring the full name.
    const out = {}
    const firstNameCounts = {}
    for (const p of profiles) {
      if (!p.full_name) continue
      const full = p.full_name.toLowerCase()
      out[full] = p
      const first = full.split(' ')[0]
      firstNameCounts[first] = (firstNameCounts[first] || 0) + 1
    }
    for (const p of profiles) {
      if (!p.full_name) continue
      const first = p.full_name.split(' ')[0].toLowerCase()
      if (firstNameCounts[first] === 1) out[first] = p
    }
    return out
  }, [profiles])

  useEffect(() => { fetchActivity() }, [ticketId])

  async function fetchActivity() {
    setLoading(true)
    const { data } = await supabase
      .from('ticket_activity')
      .select('id, ticket_id, author_id, kind, body, meta, edited_at, deleted_at, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
    setEvents(data || [])

    // Mark any unread mentions on this ticket as read. Fire-and-forget;
    // the user seeing the ticket implicitly means they've seen the mentions.
    if (currentUserId) {
      await supabase.from('ticket_mentions')
        .update({ read_at: new Date().toISOString() })
        .eq('ticket_id', ticketId)
        .eq('mentioned_id', currentUserId)
        .is('read_at', null)
    }

    setLoading(false)
  }

  // Detect @-mentions as the user types so we can pop a lightweight
  // autocomplete. We find the last @ before the caret and look at
  // what's been typed since.
  function updateMentionMenu(value, caret) {
    const slice = value.slice(0, caret)
    const atIdx = slice.lastIndexOf('@')
    if (atIdx === -1) { setMentionMenu(null); return }
    const between = slice.slice(atIdx + 1)
    // Close if the fragment has a space-space (end of name) or is too long.
    if (/\s{2,}/.test(between) || between.length > 30) { setMentionMenu(null); return }
    // Also close if the char before '@' isn't whitespace/start — avoids
    // triggering on email addresses etc.
    if (atIdx > 0 && !/\s/.test(slice[atIdx - 1])) { setMentionMenu(null); return }
    setMentionMenu({ index: atIdx, query: between.toLowerCase() })
  }

  function suggestionsFor(query) {
    if (!query && profiles.length <= 6) return profiles.slice(0, 6)
    const q = (query || '').toLowerCase()
    return profiles
      .filter(p => (p.full_name || '').toLowerCase().includes(q))
      .slice(0, 6)
  }

  function insertMention(profile) {
    if (!mentionMenu) return
    const before = draft.slice(0, mentionMenu.index)
    // Skip past whatever fragment they were typing after the '@'
    const afterStart = mentionMenu.index + 1 + mentionMenu.query.length
    const after = draft.slice(afterStart)
    const name = profile.full_name || ''
    const next = `${before}@${name} ${after}`
    setDraft(next)
    setMentionMenu(null)
    // Restore focus + move caret to just after the inserted mention
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + 1 + name.length + 1
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(pos, pos)
      }
    }, 0)
  }

  // Extract mentioned profile IDs from a comment body. We run the same
  // longest-match logic as the renderer to stay consistent.
  function extractMentions(body) {
    const hits = new Set()
    const parts = body.split(/(@[a-zA-Z][a-zA-Z0-9_.\-' ]{1,40})/g)
    for (const part of parts) {
      if (!part.startsWith('@')) continue
      const q = part.slice(1).trim().toLowerCase()
      let match = null
      for (const [norm, prof] of Object.entries(profilesByName)) {
        if (q.startsWith(norm) && (!match || norm.length > match.norm.length)) {
          match = { norm, prof }
        }
      }
      if (match) hits.add(match.prof.id)
    }
    // Don't ping yourself.
    hits.delete(currentUserId)
    return [...hits]
  }

  async function postComment() {
    const body = draft.trim()
    if (!body || posting) return
    setPosting(true)
    try {
      const { data, error } = await supabase
        .from('ticket_activity')
        .insert({ ticket_id: ticketId, author_id: currentUserId, kind: 'comment', body })
        .select('id, ticket_id, author_id, kind, body, meta, edited_at, deleted_at, created_at')
        .single()
      if (error) { alert('Post failed: ' + error.message); return }

      const mentionedIds = extractMentions(body)
      if (mentionedIds.length > 0) {
        await supabase.from('ticket_mentions').insert(
          mentionedIds.map(id => ({
            activity_id: data.id, ticket_id: ticketId, mentioned_id: id,
          }))
        )
      }

      setEvents(prev => [...prev, data])
      setDraft('')
    } finally {
      setPosting(false)
    }
  }

  async function saveEdit(id) {
    const body = editDraft.trim()
    if (!body) return
    const { data, error } = await supabase
      .from('ticket_activity')
      .update({ body, edited_at: new Date().toISOString() })
      .eq('id', id)
      .select('id, ticket_id, author_id, kind, body, meta, edited_at, deleted_at, created_at')
      .single()
    if (error) { alert('Save failed: ' + error.message); return }
    setEvents(prev => prev.map(e => e.id === id ? data : e))
    setEditingId(null); setEditDraft('')
  }

  async function softDelete(id) {
    if (!window.confirm('Delete this comment?')) return
    const { data, error } = await supabase
      .from('ticket_activity')
      .update({ deleted_at: new Date().toISOString(), body: null })
      .eq('id', id)
      .select('id, ticket_id, author_id, kind, body, meta, edited_at, deleted_at, created_at')
      .single()
    if (error) { alert('Delete failed: ' + error.message); return }
    setEvents(prev => prev.map(e => e.id === id ? data : e))
  }

  if (loading) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading activity…</div>

  const visibleEvents = events  // Keep deleted rows so the timeline shows "[deleted]"

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
        Activity
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        {visibleEvents.map(e => {
          if (e.kind === 'comment') {
            const author = e.author_id ? profilesById[e.author_id] : null
            const authorName = author?.full_name || 'Unknown'
            const isMine = e.author_id === currentUserId
            const isAdmin = currentUserRole === 'admin'
            const isDeleted = !!e.deleted_at
            // Authors can edit their own comments for EDIT_WINDOW_MS. Admins
            // can edit any comment at any time — handy for fixing typos on
            // behalf of someone who's already moved on. An admin edit still
            // stamps edited_at so the UI shows "edited".
            const canEdit = !isDeleted && (
              isAdmin || (isMine && (Date.now() - new Date(e.created_at).getTime()) < EDIT_WINDOW_MS)
            )
            // Same split for delete: authors always, admins always. Admin
            // delete remains soft — the timeline still shows [comment deleted]
            // rather than making a whole row vanish.
            const canDelete = !isDeleted && (isMine || isAdmin)
            const isEditing = editingId === e.id
            return (
              <div key={e.id} style={{
                background: 'var(--bg-secondary)', borderRadius: 6,
                padding: '8px 10px', fontSize: 13,
                opacity: isDeleted ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontWeight: 600 }}>{authorName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {relativeTime(e.created_at)}
                    {e.edited_at && !isDeleted && ' · edited'}
                  </div>
                </div>
                {isEditing ? (
                  <div>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={editDraft}
                      onChange={ev => setEditDraft(ev.target.value)}
                      style={{ fontSize: 13 }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button className="btn btn-sm btn-primary" onClick={() => saveEdit(e.id)}>Save</button>
                      <button className="btn btn-sm" onClick={() => { setEditingId(null); setEditDraft('') }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                      {isDeleted
                        ? <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>[comment deleted]</span>
                        : renderCommentBody(e.body, profilesByName)}
                    </div>
                    {(canEdit || canDelete) && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11 }}>
                        {canEdit && (
                          <button
                            onClick={() => { setEditingId(e.id); setEditDraft(e.body || '') }}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => softDelete(e.id)}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          }

          // System events: compact one-line row with a subtle left bar.
          return (
            <div key={e.id} style={{
              fontSize: 12, color: 'var(--text-muted)',
              padding: '2px 0 2px 10px',
              borderLeft: '2px solid var(--border)',
            }}>
              {renderEventLine(e, profilesById)} · {relativeTime(e.created_at)}
            </div>
          )
        })}
      </div>

      {/* Composer */}
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          className="form-input"
          rows={3}
          placeholder="Add a comment… use @ to mention someone"
          value={draft}
          onChange={e => {
            setDraft(e.target.value)
            updateMentionMenu(e.target.value, e.target.selectionStart)
          }}
          onKeyDown={e => {
            // Cmd/Ctrl+Enter to post — familiar shortcut, and keeps
            // Enter free for multi-line comments.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault()
              postComment()
            } else if (e.key === 'Escape' && mentionMenu) {
              setMentionMenu(null)
            }
          }}
        />
        {mentionMenu && (() => {
          const items = suggestionsFor(mentionMenu.query)
          if (items.length === 0) return null
          return (
            <div style={{
              position: 'absolute', bottom: '100%', left: 0,
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              zIndex: 10, minWidth: 200, marginBottom: 4,
              maxHeight: 240, overflowY: 'auto',
            }}>
              {items.map(p => (
                <button
                  key={p.id}
                  onClick={() => insertMention(p)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 10px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 13,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <div style={{ fontWeight: 500 }}>{p.full_name || p.id.slice(0,8)}</div>
                  {p.role && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.role.replace(/_/g,' ')}</div>}
                </button>
              ))}
            </div>
          )
        })()}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {draft.length > 0 ? 'Cmd/Ctrl+Enter to post' : 'Type @ to mention someone'}
          </div>
          <button
            className="btn btn-primary btn-sm"
            disabled={!draft.trim() || posting}
            onClick={postComment}>
            {posting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  )
}
